import { useQuery } from "@tanstack/react-query";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";
import { retryNetworkFailures } from "@/lib/queryClient";

// Reads the DB-backed feature flags that are ON for the current viewer (or
// anonymously) from the `enabledFeatureFlags` query. Cached for a minute to
// match the server-side flag cache TTL; one fetch is shared across the app.
const ENABLED_FLAGS_QUERY = graphql(`
  query EnabledFeatureFlags {
    enabledFeatureFlags
  }
`);

export function useFeatureFlags() {
  // isPending, not TanStack's isLoading: a retry waiting for the browser to
  // come back online has no flags yet, and FeatureRoute would show a 404.
  const { data, isPending } = useQuery({
    queryKey: ["featureFlags"],
    queryFn: async () => {
      const res = await graphqlClient.request(ENABLED_FLAGS_QUERY);
      return res.enabledFeatureFlags;
    },
    staleTime: 60 * 1000,
    retry: retryNetworkFailures,
  });

  return { flags: data ?? [], isLoading: isPending };
}

// Convenience for a single flag. `isLoading` lets callers avoid flashing the
// disabled state (e.g. a 404) before the flag set has resolved.
export function useFeatureFlag(key: string) {
  const { flags, isLoading } = useFeatureFlags();
  return { isEnabled: flags.includes(key), isLoading };
}
